import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { GPS } from "../../util/geo";
import { CubeSections, Intermediate, RReport } from "../report/Report";
import { fCoords } from "../report/sections/util";
import { useProjectCube, useProjects } from "../state";
import { useOwnership } from "../state/ownership";

const ProjectReportData = ({ location }: { location: string }) => {
  const projectId = useMemo(() => {
    const n = parseInt(location, 10);
    return Number.isFinite(n) ? n : undefined;
  }, [location]);

  const [selectedProjectId, cube, state, error, selectProject] = useProjectCube(
    useShallow((s) => [s.projectId, s.cube, s.state, s.error, s.selectProject])
  );

  const project = useProjects((s) => s.projects?.find((p) => p.id === projectId));
  const coords = useMemo(() => {
    const parcels = project?.parcels ?? [];
    if (!parcels.length) return undefined;
    return parcels.map((p) => fCoords(p.center.coordinates as GPS, ",")).join(",");
  }, [project]);

  useEffect(() => {
    if (projectId == null) return;
    if (selectedProjectId !== projectId) selectProject(projectId);
  }, [projectId, selectedProjectId, selectProject]);

  const ownershipRows = useOwnership(
    projectId != null ? `/api/projects/${projectId}/ownership` : undefined
  );

  if (projectId == null) return null;
  if (state !== "fetched" || !cube) {
    return <Intermediate state={state} cube={cube} error={error} />;
  }
  return (
    <CubeSections cube={cube} coords={coords} ownership={{ rows: ownershipRows, named: true }} />
  );
};

export const ProjectDetailView = () => {
  const { id } = useParams();
  const numericId = id ? parseInt(id, 10) : null;
  const selectProject = useProjects((s) => s.selectProject);

  // Keep the ProjectBar in sync with the URL.
  useEffect(() => {
    selectProject(numericId && Number.isFinite(numericId) ? numericId : null);
  }, [numericId, selectProject]);

  if (!numericId || !Number.isFinite(numericId)) return null;
  return (
    <RReport location={String(numericId)} updateSection={() => {}} Component={ProjectReportData} />
  );
};
